import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { reportUserActivity } from "@/shared/helpers/userActivityHelper";
import * as ReactChartJs2 from "react-chartjs-2";

type ProcessingSetter = (_value: boolean | (() => boolean)) => void;

type ChartInstanceMap = Record<string, { resize: () => void }>;

type PdfPageSize = {
  height: number;
  width: number;
};

type PdfDocument = {
  addImage: (
    _imageData: string,
    _format: string,
    _x: number,
    _y: number,
    _width: number,
    _height: number
  ) => void;
  internal: {
    pageSize: PdfPageSize;
  };
  save: (_fileName: string) => void;
};

type Html2CanvasOptions = Parameters<typeof html2canvas>[1] & {
  letterRendering?: number;
};

const Chart = (ReactChartJs2 as any).Chart;
const JsPDF = jsPDF as any;

const waitForReportImages = async (input: HTMLElement | null) => {
  const images = Array.from(input?.querySelectorAll("img") || []);

  if (!images.length) return;

  await Promise.all(
    images.map((image) => {
      image.loading = "eager";

      if (image.complete && image.naturalWidth > 0) {
        return Promise.resolve();
      }

      if (image.decode) {
        return image.decode().catch(() => {});
      }

      return new Promise((resolve) => {
        image.onload = () => resolve(undefined);
        image.onerror = () => resolve(undefined);
      });
    })
  );
};

export default async function pdfDownloadHelper(
  processing: boolean | number,
  setProcessing: ProcessingSetter = () => {},
  elId: string,
  fileName: string
) {
  void processing;
  setProcessing(() => true);
  window.scrollTo(0, 30);
  reportUserActivity("download_pdf");
  // const mainDashboardContainer = document.getElementById('main-dashboard');
  const mainDashboardContainer = document.querySelectorAll(".container");

  for (let i = 0; i < mainDashboardContainer.length; i++) {
    mainDashboardContainer[i].classList.add("reportPdfContainer");
  }

  const chartInstances = Chart.instances as unknown as ChartInstanceMap;
  for (let id in chartInstances) {
    chartInstances[id].resize();
  }

  const input = document.getElementById(elId);

  await waitForReportImages(input);

  const reportInput = input as HTMLElement;
  const rect = reportInput.getBoundingClientRect();

  const documentHeight = Math.max(
    reportInput.scrollHeight,
    reportInput.clientHeight,
    reportInput.offsetHeight,
    rect.height + 100
  );

  let documentWidth = 1400;

  const canvasOptions: Html2CanvasOptions = {
    scale: 1,
    imageTimeout: 0,
    allowTaint: false,
    useCORS: true,
    letterRendering: 1,
    logging: true,
    scrollY: -window.scrollY + 100,
    scrollX: 0,
    x: 100,

    width: documentWidth,
    height: documentHeight,

    windowWidth: documentWidth - 70,
    windowHeight: documentHeight + 20,
    onclone: (clonedDocument) => {
      clonedDocument
        .querySelectorAll(".MuiPopover-root, .MuiPopper-root")
        .forEach((el) => el.remove());

      const reportDom = clonedDocument.getElementById("campaignReport");
      if (reportDom) {
        const allEls = reportDom.querySelectorAll<HTMLDivElement>("div");
        if (allEls) {
          for (let i = 0; i < allEls.length; i++) {
            allEls[i].style.boxShadow = "none";
          }
        }
      }

      /** Did font-changes to prevent clipping in the auto complete */
      // const campaignFilters = document.querySelector(".campaign-filters");
      // if (campaignFilters) {
      //   const monthPickerButton = campaignFilters.querySelector(
      //     ".month-picker-button"
      //   );
      //   if (monthPickerButton) {
      //     monthPickerButton.style.fontSize = "inherit";
      //   }
      //   const pdfDownloadButton = campaignFilters.querySelector(
      //     ".pdf-download-button "
      //   );
      //   if (pdfDownloadButton) {
      //     pdfDownloadButton.style.fontSize = "large";
      //   }
      //   const autoCompleteDropDowns = campaignFilters.querySelectorAll(
      //     ".MuiAutocomplete-input "
      //   );
      //   if (autoCompleteDropDowns && autoCompleteDropDowns?.length) {
      //     autoCompleteDropDowns.forEach((el) => {
      //       el.style.fontSize = "large";
      //     });
      //   }
      // }
    },
  };

  await html2canvas(reportInput, canvasOptions)
    .then((canvas) => {
      for (let i = 0; i < mainDashboardContainer.length; i++) {
        mainDashboardContainer[i].classList.remove("reportPdfContainer");
      }
      const imgData = canvas.toDataURL("image/jpeg", "0.95");

      /************************************** */

      const pdfWidth = canvas.width;
      const pdfHeight = canvas.height;

      const orientation = pdfWidth >= pdfHeight ? "l" : "p";

      const pdf = new JsPDF({
        orientation,
        unit: "px",
        hotfixes: ["px_scaling"],
        format: [canvas.width, canvas.height],
      }) as PdfDocument;

      pdf.internal.pageSize.width = pdfWidth;
      pdf.internal.pageSize.height = pdfHeight;

      pdf.addImage(imgData, "JPEG", 50, 20, pdfWidth, pdfHeight - 50);

      /*********Saving***************** */
      pdf.save(`${fileName}.pdf`);
      reportInput.style.cssText = "width:auto";
      setProcessing(false);
    })
    .catch(() => {
      setProcessing(false);
    })
    .finally(() => {
      setProcessing(false);
    });
}
